import type { Application } from '@/types';
import {
  isApplicationAwaitingUpgrade,
  isApplicationPipelineRecentlyInFlight,
  isApplicationReadyToProcess,
} from '@/lib/pendingProcessing';

/** Dashboard pipeline buckets — mutually exclusive, aligned with Applications filters. */
export type DashboardPipelineBucket =
  | 'APPLIED_NEW'
  | 'READY_TO_PROCESS'
  | 'PENDING_UPGRADE'
  | 'PIPELINE_RUNNING'
  | 'PIPELINE_STUCK'
  | 'AWAITING_DECISION'
  | 'PIPELINE_ERROR'
  | 'HIRED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'OFFER_DECLINED';

export type PipelineStatusCounts = Record<DashboardPipelineBucket, number>;

export const EMPTY_PIPELINE_STATUS_COUNTS = (): PipelineStatusCounts => ({
  APPLIED_NEW: 0,
  READY_TO_PROCESS: 0,
  PENDING_UPGRADE: 0,
  PIPELINE_RUNNING: 0,
  PIPELINE_STUCK: 0,
  AWAITING_DECISION: 0,
  PIPELINE_ERROR: 0,
  HIRED: 0,
  REJECTED: 0,
  WITHDRAWN: 0,
  OFFER_DECLINED: 0,
});

/** Applications filter query value for each bucket (undefined = no dedicated filter). */
export const PIPELINE_BUCKET_STATUS_FILTER: Record<DashboardPipelineBucket, string> = {
  APPLIED_NEW: 'APPLIED_NEW',
  READY_TO_PROCESS: 'READY_TO_PROCESS',
  PENDING_UPGRADE: 'PENDING_UPGRADE',
  PIPELINE_RUNNING: 'PIPELINE_RUNNING',
  PIPELINE_STUCK: 'PIPELINE_STUCK',
  AWAITING_DECISION: 'AWAITING_DECISION',
  PIPELINE_ERROR: 'PIPELINE_ERROR',
  HIRED: 'HIRED',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN',
  OFFER_DECLINED: 'OFFER_DECLINED',
};

/** Display order — action-first, then pipeline, then outcomes. */
export const PIPELINE_STATUS_DISPLAY_ORDER: DashboardPipelineBucket[] = [
  'AWAITING_DECISION',
  'READY_TO_PROCESS',
  'PENDING_UPGRADE',
  'PIPELINE_ERROR',
  'PIPELINE_STUCK',
  'PIPELINE_RUNNING',
  'APPLIED_NEW',
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
  'OFFER_DECLINED',
];

export type PipelineStatusTab = 'action' | 'pipeline' | 'outcomes';

/** Tab groups for the dashboard pipeline status panel. */
export const PIPELINE_STATUS_TAB_BUCKETS: Record<PipelineStatusTab, DashboardPipelineBucket[]> = {
  action: [
    'AWAITING_DECISION',
    'READY_TO_PROCESS',
    'PENDING_UPGRADE',
    'PIPELINE_ERROR',
    'PIPELINE_STUCK',
  ],
  pipeline: ['PIPELINE_RUNNING', 'APPLIED_NEW'],
  outcomes: ['HIRED', 'REJECTED', 'WITHDRAWN', 'OFFER_DECLINED'],
};

export function sumPipelineTabCounts(
  counts: PipelineStatusCounts,
  tab: PipelineStatusTab,
): number {
  return PIPELINE_STATUS_TAB_BUCKETS[tab].reduce((sum, bucket) => sum + counts[bucket], 0);
}

export function defaultPipelineStatusTab(counts: PipelineStatusCounts): PipelineStatusTab {
  if (sumPipelineTabCounts(counts, 'action') > 0) return 'action';
  if (sumPipelineTabCounts(counts, 'pipeline') > 0) return 'pipeline';
  return 'outcomes';
}

export function classifyApplicationPipelineBucket(
  app: Pick<Application, 'state' | 'updatedAt' | 'appliedAt'>,
  hasProcessingCredits?: boolean,
): DashboardPipelineBucket {
  const { state } = app;

  if (state === 'HIRED') return 'HIRED';
  if (state === 'REJECTED') return 'REJECTED';
  if (state === 'WITHDRAWN') return 'WITHDRAWN';
  if (state === 'OFFER_DECLINED') return 'OFFER_DECLINED';
  if (state === 'PIPELINE_ERROR') return 'PIPELINE_ERROR';
  if (state === 'AWAITING_DECISION') return 'AWAITING_DECISION';

  if (state === 'PIPELINE_RUNNING') {
    return isApplicationPipelineRecentlyInFlight(app) ? 'PIPELINE_RUNNING' : 'PIPELINE_STUCK';
  }

  if (hasProcessingCredits === undefined) {
    if (state === 'READY_TO_PROCESS') return 'READY_TO_PROCESS';
    if (state === 'PENDING_UPGRADE') return 'PENDING_UPGRADE';
  } else {
    if (isApplicationReadyToProcess(app, hasProcessingCredits)) return 'READY_TO_PROCESS';
    if (isApplicationAwaitingUpgrade(app, hasProcessingCredits)) return 'PENDING_UPGRADE';
  }

  if (state === 'APPLIED_NEW') return 'APPLIED_NEW';

  return 'APPLIED_NEW';
}

/** Outcome buckets still count archived apps; action/pipeline buckets do not. */
const PIPELINE_OUTCOME_BUCKETS: ReadonlySet<DashboardPipelineBucket> = new Set([
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
  'OFFER_DECLINED',
]);

export function computePipelineStatusCounts(
  apps: Application[],
  hasProcessingCredits?: boolean,
): PipelineStatusCounts {
  const counts = EMPTY_PIPELINE_STATUS_COUNTS();
  for (const app of apps) {
    const bucket = classifyApplicationPipelineBucket(app, hasProcessingCredits);
    if (app.archived === true && !PIPELINE_OUTCOME_BUCKETS.has(bucket)) continue;
    counts[bucket] += 1;
  }
  return counts;
}
